<template>
  <div class="copywrap copy" @mouseover="showHint" @mouseleave="hideHint" @click.prevent="doCopy">
    <el-button class="copyButton" icon="far fa-copy" />
    <div ref="copymessage" class="copymessage copy">
      {{ copyMsg }}
    </div>
    <div class="hintWrapper">
      <div v-if="status" class="hintButton copy">
        {{ hint }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  additionalOptions,
  clearFromVideoThumbnail,
  clearFromModelThumbnail,
  clearFromZoomProperties,
  getVideoThumbnailOption,
  getModelThumbnailOption,
  paramsFromQueryString,
} from "@/utils/utils";
import { getScript } from "@/utils/script-utils";
import {
  codeSpinButtonsStyleHTML,
  cSpace,
  getCodeCSSFromOptions,
  arrowsCSS,
  imageFadeIn,
} from "@/utils/css-utils";
import { mapGetters, mapState } from "vuex";
import { hintMixin } from "@/mixins/hint.mixin";
import { cButton, codeSpinButtonsScript } from "@/utils/render-utils";
import spinControlIconStyle from "@/assets/spin_control_icon_style.json";
import { filterByVideoOptions } from "@/utils/filter-options";
import { prefetchCodeHTMLComputed } from "@/utils/prefetch";
import { getDataTypeZoomAttribute } from "@/utils/zoom-utils";

export default {
  name: "ActionCopy",
  mixins: [hintMixin],
  props: {
    hint: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      copyMsg: "",
    };
  },
  watch: {
    copyActionStatus(value) {
      if (value) {
        this.doCopy();
      }
    },
  },
  computed: {
    ...mapState([
      "mode",
      "settings",
      "enableMobileOptions",
      "viewSMVMode",
      "inlineModeSMV/inlineModeSMV",
    ]),
    ...mapGetters({
      videoThumbnailStatus: "video/videoThumbnailStatus",
      modelThumbnailStatus: "model/modelThumbnailStatus",
      inlineModeSMV: "inlineModeSMV/inlineModeSMV",
      spinControlPanelStatus: "spinControl/spinControlPanelStatus",
      selectedPosition: "spinControl/spinControlSelectedPosition",
      selectedIconsStyle: "spinControl/spinControlSelectedIconsStyle",
      spinControlBackground: "spinControl/spinControlBackground",
      spinControlIconsColor: "spinControl/spinControlIconsColor",
      selectedButtons: "spinControl/spinSelectedButtons",
      size: "spinControl/size",
      scriptByType: "script/scriptByType",
      scriptComponents: "script/components",
      scriptType: "script/scriptType",
      copyActionStatus: "copy/doCopyAction",
    }),
    prefetchCodeHTMLComputed,
    getOptionsCSSCode() {
      return getCodeCSSFromOptions(this.settings.optionsCSS, false);
    },

    codeButtons() {
      return this.selectedButtons.map((b) => this.codeButtonWithStyle(b)).join("");
    },

    codeInfoBlock() {
      return `\n${cSpace(4)}<div id="info" style="display: none;"></div>`;
    },

    imageFadeStyles() {
      if (this.settings.options.includes("single.image.fadein:false;")) {
        return imageFadeIn().replaceAll(/(<[^>]*>|&nbsp;)/gi, "");
      }
      return "";
    },

    videoTitleAndDescription() {
      let [title, description] = filterByVideoOptions(this.settings.options);

      if (title) title = ` data-title="${title.trim()}"`;
      if (description) description = ` data-description="${description.trim()}"`;

      return `${title}${description}`;
    },

    computedArrowCss() {
      return arrowsCSS().replaceAll(/(<[^>]*>|&nbsp;)/g, "");
    },
    containerStyles() {
      const containerStyles = this.settings.optionsCSS.filter(
        (it) => it.type === "container-styles"
      );
      if (containerStyles.length) {
        const stylesString = containerStyles
          .map((it) => `${it.parameter}${it.separator} ${it.value}`)
          .join("; ");
        return ` style="${stylesString}"`;
      }
      return "";
    },
    codeSpinControlStyles() {
      return (
        `\n<style>` +
        this.getSpinControlImportCode +
        this.computedArrowCss +
        this.getSpinControlButtonsCSSCode +
        this.getOptionsCSSCode +
        `\n</style>`
      );
    },
    getSpinControlImportCode() {
      const icon = spinControlIconStyle.find((e) => e.value == this.selectedIconsStyle);
      return `\n${cSpace(2)}@import url(${icon.url});\n`;
    },
    getSpinControlButtonsCSSCode() {
      return codeSpinButtonsStyleHTML(
        this.selectedPosition,
        this.selectedIconsStyle,
        this.spinControlBackground,
        this.spinControlIconsColor,
        this.size
      );
    },
    codeSpinButtonsScript() {
      return codeSpinButtonsScript(this.selectedButtons);
    },
    codeData() {
      let options = this.videoThumbnailStatus
        ? clearFromZoomProperties(
            clearFromVideoThumbnail(this.settings.options, this.videoThumbnailStatus)
          )
        : clearFromZoomProperties(this.settings.options);

      options = this.modelThumbnailStatus
        ? clearFromZoomProperties(
            clearFromModelThumbnail(this.settings.options, this.modelThumbnailStatus)
          )
        : clearFromZoomProperties(this.settings.options);

      const optionsMobile = this.settings.optionsMobile;
      const videoThumbnailOption = this.videoThumbnailStatus
        ? `data-options="thumbnail: ${getVideoThumbnailOption(this.settings.options)}"`
        : "";

      const modelThumbnailOption = this.modelThumbnailStatus
        ? `data-options="thumbnail:${getModelThumbnailOption(this.settings.options)}"`
        : "";

      let isImageFadeStylesLength = this.imageFadeStyles.length;
      let data = "";
      let mobileData = "";

      const sirvVariable = "var SirvOptions";
      const sirvMobileVariable = "var SirvMobileOptions";
      const key = "data-options";
      const mobileKey = "data-mobile-options";

      if (this.mode === "script") {
        const iterateAndFormattedArray = (obj) => {
          Object.keys(obj).map((key) => {
            if (Array.isArray(obj[key])) {
              obj[key] = Array.from(obj[key]);
            }

            if (typeof obj[key] === "object") {
              iterateAndFormattedArray(obj[key]);
            }
          });
          return obj;
        };

        data = iterateAndFormattedArray(
          paramsFromQueryString(additionalOptions(clearFromZoomProperties(this.settings.options)))
        );

        let dataString = JSON.stringify(data, null, 2)
          .replace(/\"(\w+)\":/g, "$1:")
          .replace(/\"/g, "'")
          .replace(/'\[/g, "\[")
          .replace(/\]\'/g, "\]");

        mobileData = paramsFromQueryString(additionalOptions(optionsMobile));
        let dataMobileString = JSON.stringify(mobileData, null, 2)
          .replace(/\"(\w+)\":/g, "$1:")
          .replace(/\"/g, "'");

        let dataStringOptions = dataString !== "{}" ? dataString : "";
        let mobileDataStringOptions = dataMobileString !== "{}" ? dataMobileString : "";
        let renderScriptTag =
          dataStringOptions.length !== 0 || mobileDataStringOptions.length !== 0 ? "<script>" : "";
        let renderCloseScriptTag =
          dataStringOptions.length !== 0 || mobileDataStringOptions.length !== 0
            ? "<\/script\>"
            : "";
        let renderOptions =
          dataStringOptions.length !== 0 ? `${sirvVariable} = ${dataString}\n` : "";
        let renderMobileOptions =
          dataMobileString !== "{}" ? `${sirvMobileVariable} = ${mobileDataStringOptions}\n` : "";

        return `${renderScriptTag}\n${renderOptions}${renderMobileOptions}${renderCloseScriptTag}`;
      } else {
        let dataOptions = options.length !== 0 ? ` ${key}="${options}"` : "";
        let dataMobileOptions =
          optionsMobile.length !== 0 ? ` ${mobileKey}="${optionsMobile}"` : "";
        let dataView = ` data-src="${this.settings.smv[0].src}"`;
        let dataInlineView = ` data-src="${this.settings.smv.map((item) => item.src).join(", ")}"`;

        const o = !this.settings.options.length
          ? null
          : this.settings.options.split(";").find((e) => e.indexOf("video.thumbnail") >= 0) ||
            this.settings.options.split(";").find((e) => e.indexOf("model.thumbnail") >= 0);
        const videoTemplate = !o ? "" : `data-options="${o.trim().replace("video.", "")}" `;
        const modelTemplate = !o ? "" : `data-options="${o.trim().replace("model.", "")}" `;
        const arrowsStyles = this.settings.optionsCSS.find(
          (e) => e.type !== undefined && e.type.startsWith("arrows")
        )
          ? this.computedArrowCss
          : "";

        if (
          !this.viewSMVMode &&
          !this.videoThumbnailStatus &&
          !this.modelThumbnailStatus &&
          !this.spinControlPanelStatus
        ) {
          data = `${this.prefetchCodeHTMLComputed}`;
          
          if (this.settings.smv.length === 1 && this.settings.smv[0].type === "model") {
            data =
              `${data}<div class="Sirv"${this.containerStyles}>\n` +
              `${cSpace(2)}<div data-src="${this.settings.smv[0].src}"${modelTemplate ? ` ${modelTemplate}` : ""}></div>\n` +
              `</div> \n${getScript(this.scriptType, this.scriptComponents)}\n`;
          } else {
            data =
              `${data}<div class="Sirv"` +
              `${dataOptions}${dataMobileOptions}${this.containerStyles}` +
              `>\n`;

            for (let i = 0; i < this.settings.smv.length; i++) {
              data =
                `${data}${cSpace(2)}<div ${
                  this.settings.smv[i].type === "video"
                    ? `${videoTemplate}`
                    : this.settings.smv[i].type === "model"
                    ? `${modelTemplate}`
                    : ""
                }data-src="${this.settings.smv[i].src}"${
                  this.settings.smv[i].type === "video" ? this.videoTitleAndDescription : ""
                }${this.getHtmlZoom(this.settings.smv[i].type)}` + `></div>\n`;
            }
            data = `${data}</div> \n${getScript(this.scriptType, this.scriptComponents)}\n`;
          }

          if (this.getOptionsCSSCode !== "") {
            data = `${data}<style>${arrowsStyles}${this.getOptionsCSSCode}\n</style>`;
          }
        } else if (this.videoThumbnailStatus) {
          data =
            `${data}<div class="Sirv"` +
            `${dataOptions}${dataMobileOptions}${this.containerStyles}` +
            `>\n`;
          for (let i = 0; i < this.settings.smv.length; i++) {
            data =
              `${data} <div data-src="${this.settings.smv[i].src}"${this.getHtmlZoom(
                this.settings.smv[i].type
              )} ${this.settings.smv[i].type === "video" ? `${videoThumbnailOption}` : ""}` +
              `></div>\n`;
          }
          data = `${data}</div> \n${getScript(this.scriptType, this.scriptComponents)}\n`;
          if (this.getOptionsCSSCode !== "") {
            data = `${data}<style>${arrowsStyles}${this.getOptionsCSSCode}\n</style>`;
          }
        } else if (this.modelThumbnailStatus) {
          if (this.settings.smv.length === 1 && this.settings.smv[0].type === "model") {
            data =
              `${data}<div class="Sirv"${this.containerStyles}>\n` +
              `${cSpace(2)}<div data-src="${this.settings.smv[0].src}"${modelThumbnailOption ? ` ${modelThumbnailOption}` : ""}></div>\n` +
              `</div> \n${getScript(this.scriptType, this.scriptComponents)}\n`;
          } else {
            data =
              `${data}<div class="Sirv"` +
              `${dataOptions}${dataMobileOptions}${this.containerStyles}` +
              `>\n`;
            for (let i = 0; i < this.settings.smv.length; i++) {
              data =
                `${data} <div data-src="${this.settings.smv[i].src}"${this.getHtmlZoom(
                  this.settings.smv[i].type
                )}${this.settings.smv[i].type === "model" ? ` ${modelThumbnailOption}` : ""}` +
                `></div>\n`;
            }
            data = `${data}</div> \n${getScript(this.scriptType, this.scriptComponents)}\n`;
          }
          
          if (this.getOptionsCSSCode !== "") {
            data = `${data}<style>${arrowsStyles}${this.getOptionsCSSCode}\n</style>`;
          }
        } else if (this.inlineModeSMV) {
          if (this.settings.options.indexOf("zoom.enable:false;") >= 0) {
            dataOptions = options.length !== 0 ? ` ${key}="${options}; zoom.ratio: 0;"` : "";
          }

          data =
            `${data}<div class="Sirv"` +
            `${dataInlineView}${dataOptions}${dataMobileOptions}${this.containerStyles}` +
            `>`;
          data = `${data}</div>\n${getScript(this.scriptType, this.scriptComponents)}\n`;
          if (this.getOptionsCSSCode !== "") {
            data = `${data}<style>${this.getOptionsCSSCode}\n</style>`;
          }
        } else if (this.viewSMVMode) {
          if (this.settings.options.indexOf("zoom.enable:false;") >= 0) {
            dataOptions = options.length !== 0 ? ` ${key}="${options}; zoom.ratio: 0;"` : "";
          }

          data =
            `${data}<div class="Sirv"` +
            `${dataView}${dataOptions}${dataMobileOptions}${this.containerStyles}` +
            `>`;
          data = `${data}</div>\n${getScript(this.scriptType, this.scriptComponents)}\n`;
          if (this.getOptionsCSSCode !== "") {
            data = `${data}<style>${this.getOptionsCSSCode}\n</style>`;
          }
        } else if (this.spinControlPanelStatus) {
          data =
            `<div class="viewer">` +
            `\n${cSpace(2)}<div id="smv" class="Sirv"` +
            `${dataOptions}${dataMobileOptions}${this.containerStyles}` +
            `>\n`;

          for (let i = 0; i < this.settings.smv.length; i++) {
            data =
              this.settings.smv.length > 1 && i !== this.settings.smv.length - 1
                ? `${data}${cSpace(4)}<div data-src="${this.settings.smv[i].src}"${this.getHtmlZoom(
                    this.settings.smv[i].type
                  )}></div>\n`
                : `${data}${cSpace(4)}<div data-src="${this.settings.smv[i].src}"${this.getHtmlZoom(
                    this.settings.smv[i].type
                  )}></div>`;
          }

          data =
            `${data}` +
            `\n${cSpace(2)}</div>` +
            `\n${cSpace(2)}<div class="spin-buttons">` +
            this.codeButtons +
            this.codeInfoBlock +
            `\n${cSpace(2)}</div>` +
            `\n</div>`;

          data = `${data}\n${getScript(this.scriptType, this.scriptComponents)}`;
          data = `${data}\n` + this.codeSpinButtonsScript;
          if (this.getOptionsCSSCode !== "" || this.spinControlPanelStatus) {
            data = `${data}${this.codeSpinControlStyles}`;
          }
        }

        return data;
      }
    },
  },
  methods: {
    codeButtonWithStyle(button) {
      const space = 4;
      if (button === "play") {
        return (
          `\n${cSpace(space)}${cButton(this.selectedIconsStyle, button)}` +
          `\n${cSpace(space)}${cButton(this.selectedIconsStyle, "pause")}`
        );
      }
      return `\n${cSpace(space)}${cButton(this.selectedIconsStyle, button)}`;
    },
    getHtmlZoom(type) {
      return getDataTypeZoomAttribute(
        this.settings?.options,
        type,
        this.settings?.optionsMobile
      );
    },
    doCopy() {
      this.status = false;
      this.$copyText(this.codeData).then(
        (e) => {
          this.copyMsg = "Copied";
          this.$store.dispatch("copy/setCopyAction", false);
          setTimeout(() => {
            this.copyMsg = "";
          }, 3000);
        },
        function (e) {
          alert("Can not copy");
        }
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.hintButton.copy {
  min-width: 90px;
  cursor: pointer;
}
.copywrap.copy > .copymessage.copy {
  right: -15px;
}
</style>
